import classNames from "classnames";
import { camelizeKeys } from "humps";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import toString from "lodash/toString";
import { ActionDiv } from "PFComponents/containers";
import { Flex, Stack, StackProps } from "PFComponents/containers/flex";
import { IconSize, IconSizeTable } from "PFComponents/icon/icon.utils";
import { ProfileAvatar } from "PFComponents/profile_avatar/profile_avatar";
import { SuspendedPill } from "PFComponents/suspended_pill";
import { Typography } from "PFComponents/typography";
import { getJobTitle, getProfileName } from "PFCore/helpers/profile";
import { useUnbiasedProfileName } from "PFCore/hooks/use_unbiased_profile_name";
import { BasicProfile, Profile, ProfileMinimized } from "PFTypes";
import { useCallback, useMemo } from "react";

import css from "./profile_item.module.scss";

type ProfileItemProps = {
  classes?: {
    root?: string;
    highlighted?: string;
    content?: string;
  };
  profile: BasicProfile | Profile | ProfileMinimized;
  style?: React.CSSProperties;
  avatarSize?: IconSize;
  displayNameOnly?: boolean;
  displayAvatar?: boolean;
  displayJobTitle?: boolean;
  displayEmail?: boolean;
  displaySuspendedDate?: boolean;
  displaySuspendedDateBelow?: boolean;
  splitInfo?: boolean;
  isHighlighted?: boolean;
  rootFlexProps?: StackProps;
  onClick?: VoidFunction;
  clipOverflow?: boolean;
  isUnbiasedMode?: boolean;
};

const calculateJobTitle = (profile: BasicProfile | Profile): string => {
  const { jobTitle } = profile as BasicProfile;
  const { positions } = profile as Profile;

  if (isString(jobTitle)) {
    return jobTitle;
  }

  if (positions) {
    return toString(getJobTitle(profile as Profile));
  }

  return "";
};

export const ProfileItem = ({
  classes = {},
  profile,
  style,
  avatarSize,
  displayNameOnly,
  displayAvatar = true,
  displayJobTitle = true,
  displayEmail = true,
  displaySuspendedDate = false,
  displaySuspendedDateBelow = true,
  clipOverflow = true,
  splitInfo,
  isHighlighted,
  rootFlexProps = {},
  onClick,
  isUnbiasedMode = false
}: ProfileItemProps) => {
  const camelizedProfile = useMemo(() => camelizeKeys(profile) as BasicProfile | Profile, [profile]); // TODO: [PROF-5746] Remove "camelizeKeys" when all ProfileItem usages get camelized profile prop
  const { getUnbiasedProfileName } = useUnbiasedProfileName();
  const { text, suspendedAt } = camelizedProfile as BasicProfile;
  const name = isUnbiasedMode
    ? getUnbiasedProfileName(camelizedProfile, !!isUnbiasedMode)
    : text || getProfileName(camelizedProfile);
  const calculatedJobTitle = calculateJobTitle(camelizedProfile);
  const title = compact([name, camelizedProfile.email]).join(" ");
  const additionalInfo =
    !displayNameOnly && !isUnbiasedMode
      ? compact([displayJobTitle && calculatedJobTitle, displayEmail && camelizedProfile.email])
      : [];

  const WrapperComponent = useCallback(
    ({ children }) =>
      onClick ? (
        <ActionDiv onClick={onClick} aria-label={title}>
          {children}
        </ActionDiv>
      ) : (
        <>{children}</>
      ),
    [onClick, title]
  );

  const displayProfileAvatar = !isUnbiasedMode && displayAvatar && (profile.avatar || !!suspendedAt);
  const displaySuspendedPill = !displayNameOnly && !!displaySuspendedDate && !!suspendedAt;

  return (
    <WrapperComponent>
      <Stack className={classNames(css.root, classes.root)} {...(rootFlexProps ?? {})}>
        <Flex alignItems="center" justifyContent="start" style={style} title={title}>
          {displayProfileAvatar && (
            <ProfileAvatar
              profile={camelizedProfile as ProfileMinimized}
              size={avatarSize ? IconSizeTable[avatarSize] : 40}
              isHighlighted={isHighlighted}
              highlightedClassName={classes.highlighted}
            />
          )}
          <div
            className={classNames(css.dropdownItem, classes.content, {
              [css.noAvatar]: !profile.avatar || !displayAvatar
            })}
          >
            <Typography clipOverflow={clipOverflow} variant="bodyBold">
              {name}
            </Typography>
            {!isEmpty(additionalInfo) &&
              (splitInfo ? (
                <Stack gap="spacingXs">
                  {additionalInfo.map((info, index) => (
                    <Typography key={index} clipOverflow={clipOverflow} variant="labelRegular" tag="span">
                      {info}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography clipOverflow={clipOverflow} variant="labelRegular" tag="p">
                  {additionalInfo.join(" ")}
                </Typography>
              ))}
            {displaySuspendedPill && !displaySuspendedDateBelow && (
              <SuspendedPill suspendedAt={suspendedAt} className={css.suspended} />
            )}
          </div>
        </Flex>
        {displaySuspendedPill && !!displaySuspendedDateBelow && (
          <SuspendedPill suspendedAt={suspendedAt} className={css.suspended} />
        )}
      </Stack>
    </WrapperComponent>
  );
};
